/**
 * @fileoverview Defines the capability contract for an LLM in the model registry.
 * @module types/model-capabilities
 * @description Captures what content types and features each configured LLM can
 * accept (vision, PDFs, audio, tool calling, token limits). Used to gate UI
 * affordances (file attach button), validate requests before they hit the
 * provider, and pick the right model when attachments require fallback routing.
 */

import { z } from "zod";

/**
 * PDF handling mode for a given model.
 *
 * - `"native"`: provider can ingest the PDF as a `document` / `input_file`
 *   content part directly (e.g. Anthropic Claude, OpenAI Responses API).
 * - `"extract"`: provider only accepts text — the app must extract PDF text
 *   server-side and inject it as a `text` part (vLLM-hosted models).
 * - `false`: PDFs not supported at all on this model.
 */
export const pdfModeSchema = z.union([
  z.literal("native"),
  z.literal("extract"),
  z.literal(false),
]);

/**
 * Canonical reasoning-effort ladder.
 *
 * Seven rungs covering the union of every vendor's ladder as of August 2026.
 * No provider offers all seven — OpenAI's GPT-5.6 tops out at `max` but has no
 * `minimal`, Anthropic starts at `low`, Google stops at `high`, and DeepSeek /
 * GLM / Kimi ship `low · high · max` with no `medium` at all. A model declares
 * its own subset in {@link reasoningCapabilitySchema}; this union is only the
 * shared vocabulary the app speaks internally.
 *
 * Deliberately excludes `ultra`. Where that level is documented it does not buy
 * deeper per-call reasoning — it fans work out to parallel sub-agents and
 * clamps the underlying model calls back to `xhigh`. That is delegation, which
 * this codebase already models as sub-agents, not a point on this ladder.
 */
export const reasoningEffortSchema = z.enum([
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
]);

/** One rung of the canonical ladder. */
export type ReasoningEffort = z.infer<typeof reasoningEffortSchema>;

/**
 * The ladder in ascending order. Exported because the nearest-rung projection
 * and any UI that renders the scale both need the ordering, and duplicating it
 * is how the two drift apart.
 */
export const REASONING_EFFORT_ORDER: readonly ReasoningEffort[] = [
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
] as const;

/**
 * Which wire field an OpenAI-compatible endpoint accepts for reasoning.
 *
 * Only meaningful for the providers built on the shared OpenAI-compatible
 * adapter — Qwen, Z.AI, Ollama, LM Studio, vLLM and CUSTOM. Those six share one
 * SDK adapter but not one convention, and the convention cannot be inferred
 * from the model id: the same endpoint can serve Qwen, GLM and Llama. So an
 * admin declares it, and absence means "send nothing".
 *
 * Exactly one value today, deliberately. `reasoning_effort` is the OpenAI-style
 * field, which the adapter already emits natively. The other conventions in the
 * wild — GLM's `thinking: { type }` and Qwen's
 * `chat_template_kwargs.enable_thinking` — are NOT listed, because the adapter
 * has no escape hatch for arbitrary body fields and sending them would require
 * a request-rewriting wrapper we have not built. Listing them would let an
 * admin enable a control that silently does nothing, which is worse than not
 * offering it.
 */
export const reasoningDialectSchema = z.enum(["reasoning_effort"]);

/** Wire convention for reasoning on an OpenAI-compatible endpoint. */
export type ReasoningDialect = z.infer<typeof reasoningDialectSchema>;

/**
 * What reasoning control a model actually offers.
 *
 * A discriminated union rather than a set of optional fields, because the four
 * shapes need different data and mixing them is what produces requests the
 * provider rejects — Google errors when sent both a level and a budget, and
 * OpenRouter errors when sent both `effort` and `max_tokens`.
 *
 * Absent (the field is optional) means UNKNOWN, not unsupported: the app then
 * sends no reasoning parameter at all, which is byte-identical to the requests
 * it sent before this field existed. Fail-closed by construction.
 */
export const reasoningCapabilitySchema = z.discriminatedUnion("mode", [
  /** Model has no reasoning control, or we know it ignores one. */
  z.object({ mode: z.literal("none") }),

  /**
   * Named rungs — OpenAI, Anthropic, Google (Gemini 3+), xAI, OpenRouter.
   * `levels` is the model's REAL subset, in no particular order; callers sort
   * through {@link REASONING_EFFORT_ORDER}.
   */
  z.object({
    mode: z.literal("effort"),
    levels: z.array(reasoningEffortSchema).min(1),
    default: reasoningEffortSchema.optional(),
    /**
     * False when the provider rejects a request that turns thinking off —
     * GLM-5.3 fails outright, Gemini 3.1 Pro and Grok have no off switch, and
     * Claude refuses it at `xhigh`/`max`. The UI must not offer "Off" here.
     */
    canDisable: z.boolean(),
    /**
     * OpenAI-compatible endpoints only. Ignored for first-party providers,
     * whose wire format follows from the provider itself.
     */
    dialect: reasoningDialectSchema.optional(),
  }),

  /**
   * Token budget instead of a named rung — Gemini 2.5, self-hosted Qwen.
   * Effort is projected onto a token count; see the mapping in `@taqat/ai`.
   */
  z.object({
    mode: z.literal("budget"),
    maxBudgetTokens: z.number().int().positive(),
    default: z.number().int().nonnegative().optional(),
    canDisable: z.boolean(),
  }),

  /** On/off only, no gradations — DeepSeek's `thinking` object, most self-hosted. */
  z.object({
    mode: z.literal("toggle"),
    default: z.boolean().optional(),
    canDisable: z.boolean(),
    /** OpenAI-compatible endpoints only. See {@link reasoningDialectSchema}. */
    dialect: reasoningDialectSchema.optional(),
  }),
]);

/** What reasoning control a model offers. See {@link reasoningCapabilitySchema}. */
export type ReasoningCapability = z.infer<typeof reasoningCapabilitySchema>;

/**
 * Zod schema describing the full capability contract for an LLM.
 * Stored as a JSONB column on `workspace_custom_models` and `credit_providers`,
 * and validated at every write boundary so the column shape stays sound.
 */
export const modelCapabilitiesSchema = z.object({
  /** Always true — every chat model accepts text input. */
  text: z.literal(true),

  /** Whether the model accepts image inputs (vision). */
  images: z.boolean(),

  /** Whether the model accepts audio inputs. */
  audio: z.boolean(),

  /** PDF/document handling strategy — see {@link pdfModeSchema}. */
  pdf: pdfModeSchema,

  /**
   * Whether the model accepts arbitrary document MIME types beyond PDFs
   * (DOCX, XLSX, etc.) as native content parts. Almost always false — the app
   * extracts these to text regardless of the model.
   */
  documents: z.boolean(),

  /** Whether the model supports OpenAI-style function/tool calling. */
  toolCalling: z.boolean(),

  /** Whether the model supports streamed responses (SSE). */
  streaming: z.boolean(),

  /** Whether the model supports response_format=json_object. */
  jsonMode: z.boolean(),

  /**
   * Total usable context window in tokens (input + output combined). Used
   * by the chat compactor to decide when to summarize history before the
   * next turn would overflow. Replaces the older `maxInputTokens` /
   * `maxOutputTokens` pair — splitting the budget two ways was theoretical
   * accuracy the compaction code never used.
   */
  contextLength: z.number().int().positive(),

  /** Per-file size cap in megabytes (provider-enforced). Optional. */
  maxFileSizeMb: z.number().positive().optional(),

  /** Per-image size cap in megabytes (provider-enforced). Optional. */
  maxImageSizeMb: z.number().positive().optional(),

  /**
   * Allowlist of image MIME types the model accepts. When omitted, the app
   * assumes the common set (image/png, image/jpeg, image/webp, image/gif).
   */
  supportedImageMimeTypes: z.array(z.string()).optional(),

  /**
   * Allowlist of file MIME types the model accepts as native parts.
   * Only meaningful when `pdf === "native"` or `documents === true`.
   */
  supportedFileMimeTypes: z.array(z.string()).optional(),

  /**
   * What reasoning control this model offers, if any.
   *
   * Optional so every row written before this field existed keeps validating.
   * Absent means unknown — the app sends no reasoning parameter, exactly as it
   * did before. See {@link reasoningCapabilitySchema}.
   *
   * Distinct from the older `hasReasoning` boolean on the model row, which
   * answers "does this model show its thinking" (a display concern driving a
   * badge, a filter, and an output-token floor). This answers "what can a user
   * set" (a control concern). Conflating them is why the boolean could never
   * grow past yes/no.
   */
  reasoning: reasoningCapabilitySchema.optional(),
});

/**
 * Capability contract for an LLM. Inferred from the Zod schema so the runtime
 * validator and the static type stay in lockstep.
 */
export type ModelCapabilities = z.infer<typeof modelCapabilitiesSchema>;

/**
 * PDF handling mode literal. Re-exported so call sites don't have to import
 * the schema just to type a comparison.
 */
export type PdfMode = z.infer<typeof pdfModeSchema>;

/**
 * Conservative default applied to any model row created without explicit
 * capabilities. Treats the model as text-only with a safe context cap so we
 * never optimistically expose attach UI for an unknown model. The Add Custom
 * Model dialog overrides these defaults with more generous values (images +
 * tools enabled) — admins adding a model usually intend to use modern
 * features. This default is for legacy rows / unknown models only.
 */
export const DEFAULT_CAPABILITIES: ModelCapabilities = {
  text: true,
  images: false,
  audio: false,
  pdf: false,
  documents: false,
  toolCalling: false,
  streaming: true,
  jsonMode: false,
  contextLength: 8000,
};

/**
 * True when the model can ingest at least one non-text attachment type
 * natively. Used by the chat UI to decide whether to render the file attach
 * affordance, and by the chat handler to decide whether to inject the
 * `read_attachments` tool fallback.
 */
export function supportsNativeAttachments(caps: ModelCapabilities): boolean {
  return caps.images || caps.audio || caps.pdf === "native" || caps.documents;
}

/**
 * True when a reasoning control should be rendered for this model.
 *
 * The chat composer asks this before drawing anything: a model with no
 * declared capability, or one declared `"none"`, gets no control at all rather
 * than a disabled one. A greyed-out dial reads as a bug and invites a support
 * ticket; an absent dial reads as "this model doesn't do that".
 */
export function supportsReasoningControl(caps: ModelCapabilities): boolean {
  return caps.reasoning !== undefined && caps.reasoning.mode !== "none";
}
